import { isSessionStorageSupported } from 'lib/helpers';
import { getCandidateJwt } from '../providers/authCredentialSource';
import { isBffHardeningOn } from '../api/harness/browserHarnessFlag';
import {
  CHECKR_BFF_API_BASE,
  SESSION_STORAGE_JWT_KEY,
  SESSION_STORAGE_TOKEN_KEY,
} from '../constants';
import {
  hasVerificationSession,
  withVerificationSession,
} from './verificationSession';

export const PORTAL_TOKEN_HEADER = 'X-Candidate-Portal-Token';

export type VerificationAuthMode = 'off' | 'shadow' | 'on';

export interface CredentialState {
  portal?: { token?: string };
}

export interface VerificationAccessFailure {
  status: number;
  credentialed: boolean;
}

type RequestOptions = {
  method?: string;
  headers?: Record<string, string>;
  credentials?: RequestCredentials;
};

const AUTH_MODES = new Set<VerificationAuthMode>(['off', 'shadow', 'on']);
const ACCESS_STATUSES = new Set([401, 403, 410]);
const JSON_HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

const storedItem = (key: string) =>
  isSessionStorageSupported() ? globalThis.sessionStorage.getItem(key) : null;

// The JWT comes from the AuthProvider (ECDEX-3441: it is no longer in Redux).
// Before Layout restores a stored login after a cold page load there is none
// there yet, so fall back to the stored credentials.
export const candidateCredentialHeaders = (
  state?: CredentialState,
): Record<string, string> => {
  const jwt = getCandidateJwt() || storedItem(SESSION_STORAGE_JWT_KEY);
  if (jwt) return { Authorization: `Bearer ${jwt}` };

  const portalToken =
    state?.portal?.token || storedItem(SESSION_STORAGE_TOKEN_KEY);
  return portalToken ? { [PORTAL_TOKEN_HEADER]: portalToken } : {};
};

export const hasCandidateCredential = (state?: CredentialState) =>
  Object.keys(candidateCredentialHeaders(state)).length > 0;

const pendingModes = new Map<string, Promise<VerificationAuthMode>>();
const resolvedModes = new Map<string, VerificationAuthMode>();

const loadAuthMode = (key: string, path: string) => {
  let pending = pendingModes.get(key);
  if (!pending) {
    pending = fetch(path, { headers: { Accept: 'application/json' } })
      .then(response => (response.ok ? response.json() : null))
      .then(body => (AUTH_MODES.has(body?.auth_mode) ? body.auth_mode : 'off'))
      .catch(() => 'off' as const)
      .then(mode => {
        resolvedModes.set(key, mode);
        return mode;
      });
    pendingModes.set(key, pending);
  }
  return pending;
};

// Only a candidate who holds a credential asks, and only with BFF hardening on;
// anyone else gets the default branch's anonymous requests.
const shouldAskAuthMode = (state?: CredentialState) =>
  hasCandidateCredential(state) && isBffHardeningOn();

export const verificationAuthMode = (
  verificationId: string,
  state?: CredentialState,
): Promise<VerificationAuthMode> =>
  shouldAskAuthMode(state)
    ? loadAuthMode(
        verificationId,
        `${CHECKR_BFF_API_BASE}/verifications/${verificationId}/access`,
      )
    : Promise.resolve('off');

export const reportAuthMode = (
  reportId: string,
  state?: CredentialState,
): Promise<VerificationAuthMode> =>
  shouldAskAuthMode(state)
    ? loadAuthMode(
        `report:${reportId}`,
        `${CHECKR_BFF_API_BASE}/ssn_trace_status/access?report_id=${encodeURIComponent(
          reportId,
        )}`,
      )
    : Promise.resolve('off');

export const forgetVerificationAuthMode = (verificationId: string) => {
  pendingModes.delete(verificationId);
  resolvedModes.delete(verificationId);
};

export const clearVerificationAuthModes = () => {
  pendingModes.clear();
  resolvedModes.clear();
};

const withCandidateCredentials = <T extends RequestOptions>(
  options: T,
  state?: CredentialState,
) => ({
  ...options,
  headers: {
    ...JSON_HEADERS,
    ...options.headers,
    ...candidateCredentialHeaders(state),
  },
});

export const withVerificationCredentials = async <T extends RequestOptions>(
  verificationId: string,
  state?: CredentialState,
  options: T = {} as T,
): Promise<T> => {
  if (hasVerificationSession(verificationId)) {
    return withVerificationSession(verificationId, options);
  }
  const mode = await verificationAuthMode(verificationId, state);
  return mode === 'off' ? options : withCandidateCredentials(options, state);
};

export const withReportCredentials = async <T extends RequestOptions>(
  reportId: string,
  state?: CredentialState,
  options: T = {} as T,
): Promise<T> => {
  const mode = await reportAuthMode(reportId, state);
  return mode === 'off' ? options : withCandidateCredentials(options, state);
};

export const sentCandidateCredential = (options: RequestOptions) =>
  !!options.headers?.Authorization || !!options.headers?.[PORTAL_TOKEN_HEADER];

// Resolves to null when nothing applies, so callers send the default request.
export const verificationAxiosConfig = async (
  verificationId: string | undefined,
  state?: CredentialState,
  method = 'GET',
) => {
  if (!verificationId) return null;
  const options = await withVerificationCredentials(verificationId, state, {
    method,
  } as RequestOptions);
  if (!options.headers) return null;
  return {
    headers: options.headers,
    withCredentials: options.credentials === 'include',
  };
};

// Access failures that came from the BFF enforcing auth or a customer session.
// Anything else keeps the default branch's error handling.
export const verificationAccessFailure = (
  error: { status?: number; body?: { auth_mode?: string } } | undefined,
  verificationId: string,
  options: RequestOptions = {},
): VerificationAccessFailure | null => {
  const status = error?.status;
  if (!status || !ACCESS_STATUSES.has(status) || !isBffHardeningOn()) {
    return null;
  }
  const enforced =
    error?.body?.auth_mode === 'on' ||
    options.credentials === 'include' ||
    resolvedModes.get(verificationId) === 'on';
  return enforced
    ? { status, credentialed: sentCandidateCredential(options) }
    : null;
};
